// Web-specific login screen. Native uses LoginScreen.tsx in this folder.

import React from 'react';
import { View, Text, TextInput, TouchableOpacity, StyleSheet } from 'react-native';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { authService } from '../../services/authService';
import { api } from '../../services/api';
import { setUser, setToken } from '../../store/slices/authSlice';
import { getErrorMessage } from '../../utils/errorMessages';

const LoginScreen = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [email, setEmail] = React.useState('');
  const [password, setPassword] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await authService.login(email, password);

      // Check if response has the expected structure
      if (!response) {
        throw new Error('No response from login');
      }

      if (!response.token || !response.user) {
        throw new Error('Invalid login response structure');
      }

      // Additional validation
      if (!response.user.id) {
        throw new Error('Invalid user data in response');
      }
      
      // Store token in localStorage/AsyncStorage
      await api.setToken(response.token);
      
      // Also store token directly in localStorage for web
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.setItem('token', response.token);
      }
      
      // Set token in Redux
      dispatch(setToken(response.token));
      
      // Then set user in Redux
      try {
        dispatch(setUser({
          id: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          role: response.user.role
        }));
      } catch (reduxError) {
        throw reduxError;
      }
      
    } catch (error: unknown) {
      setError(getErrorMessage(error, 'Login failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = () => {
    // Navigate to forgot password screen
    if (typeof window !== 'undefined') {
      window.location.href = '/forgot-password';
    }
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        <Text style={styles.title}>Welcome to Exhale</Text>
        <Text style={styles.subtitle}>Home Review System</Text>
        
        <TextInput
          style={styles.input}
          placeholder="Email"
          value={email}
          onChangeText={setEmail}
          autoCapitalize="none"
          keyboardType="email-address"
          onSubmitEditing={() => {
            // Focus password field when Enter is pressed in email field
            const passwordInput = document.getElementById('password-input');
            if (passwordInput) passwordInput.focus();
          }}
        />

        <TextInput
          style={styles.input}
          placeholder="Password"
          value={password}
          onChangeText={setPassword}
          secureTextEntry
          onSubmitEditing={handleLogin}
          // @ts-ignore - web-specific prop
          id="password-input"
        />
        
        {error && <Text style={styles.error}>{error}</Text>}
        
        <TouchableOpacity 
          style={[styles.button, loading && styles.buttonDisabled]}
          onPress={handleLogin}
          disabled={loading}
        >
          <Text style={styles.buttonText}>{loading ? 'Signing In...' : 'Sign In'}</Text>
        </TouchableOpacity>
        
        <TouchableOpacity onPress={handleForgotPassword} style={styles.forgotPasswordLink}>
          <Text style={styles.forgotPasswordText}>Forgot Password?</Text>
        </TouchableOpacity>
        
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f5f5f5',
    padding: 20,
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 10,
    padding: 30,
    width: '100%',
    maxWidth: 400,
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 10,
    color: '#333',
  },
  subtitle: {
    fontSize: 16,
    textAlign: 'center',
    marginBottom: 30,
    color: '#666',
  },
  input: {
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 5,
    padding: 12,
    marginBottom: 15,
    fontSize: 16,
  },
  button: {
    backgroundColor: '#007AFF',
    borderRadius: 5,
    padding: 15,
    alignItems: 'center',
    marginTop: 10,
  },
  buttonDisabled: {
    backgroundColor: '#ccc',
  },
  buttonText: {
    color: 'white',
    fontSize: 16,
    fontWeight: '600',
  },
  error: {
    color: 'red',
    textAlign: 'center',
    marginBottom: 10,
  },
  forgotPasswordLink: {
    marginTop: 20,
    alignItems: 'center',
  },
  forgotPasswordText: {
    color: '#007AFF',
    fontSize: 14,
    textDecorationLine: 'underline',
  },
});

export default LoginScreen;
